/**
 * Ajustes de la tarifa de cobertura por mercaderia y por valor (Mike, 9-oct:
 * "We should also be able to charge differently on values and commodity
 * types"). Cada comerciante tiene su base en el setup; esto se suma encima y
 * el resultado nunca baja de 12 ni pasa de 50. Ver services/tarifa_cobertura.py.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/apiError';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mensaje = (j, fallback) => {
  const d = j && j.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(' ');
  return d.message || fallback;
};

export default function CoverageRateAdjustments({ token }) {
  const [cfg, setCfg] = useState(null);
  const [cats, setCats] = useState({});
  const [tramos, setTramos] = useState([]);
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    const r = await fetch(`${API}/insurance/coverage-rate/adjustments`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json().catch(() => null);
    if (!r.ok || !d) {
      // Antes la tarjeta desaparecia sin decir nada.
      toast.error(apiErrorMessage(d, 'Coverage rate adjustments could not be loaded.'));
      return;
    }
    setCfg(d);
    const c = {};
    for (const x of d.categories) c[x.code] = d.by_category[x.code] ?? '';
    setCats(c);
    setTramos(d.by_value.map((b) => ({ over: b.over, add: b.add })));
  }, [token]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const by_category = {};
      for (const [k, v] of Object.entries(cats)) if (v !== '' && Number(v) !== 0) by_category[k] = Number(v);
      const by_value = tramos.filter((t) => t.over !== '' && t.add !== '').map((t) => ({ over: Number(t.over), add: Number(t.add) }));
      const r = await fetch(`${API}/insurance/coverage-rate/adjustments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: nota, by_category, by_value }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(mensaje(j, 'Adjustments not saved.'));
      toast.success('Coverage rate adjustments saved');
      setNota('');
      cargar();
    } catch (e) { toast.error(e.message); }
    finally { setGuardando(false); }
  };

  if (!cfg) return null;

  return (
    <Card data-testid="coverage-rate-adjustments">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Coverage rate adjustments</CardTitle>
        <CardDescription>
          Each merchant pays its base rate (default {cfg.default_cents}¢ per $100, set in its setup below) plus these
          adjustments. The result always stays between {cfg.min_cents}¢ and {cfg.max_cents}¢. Applies to coverage A;
          the other coverages charge their own price.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>By commodity (¢ per $100, can be negative)</Label>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
            {cfg.categories.map((c) => (
              <label key={c.code} className="flex items-center gap-2 text-sm">
                <Input type="number" step="1" className="w-20" value={cats[c.code]}
                  onChange={(e) => setCats((all) => ({ ...all, [c.code]: e.target.value }))}
                  placeholder="0" data-testid={`adj-cat-${c.code}`} />
                <span className="text-slate-600 dark:text-slate-300">{c.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label>By declared value (only the highest band passed applies)</Label>
          <div className="space-y-2 mt-1">
            {tramos.map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span>Over $</span>
                <Input type="number" min="1" className="w-32" value={t.over}
                  onChange={(e) => setTramos((all) => all.map((x, j) => (j === i ? { ...x, over: e.target.value } : x)))}
                  data-testid={`adj-band-over-${i}`} />
                <span>add</span>
                <Input type="number" step="1" className="w-20" value={t.add}
                  onChange={(e) => setTramos((all) => all.map((x, j) => (j === i ? { ...x, add: e.target.value } : x)))}
                  data-testid={`adj-band-add-${i}`} />
                <span>¢</span>
                <Button size="sm" variant="ghost" onClick={() => setTramos((all) => all.filter((_, j) => j !== i))}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setTramos((all) => [...all, { over: '', add: '' }])} data-testid="adj-band-add">
              <Plus className="w-4 h-4 mr-1" /> Add value band
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[16rem]">
            <Label>Why (required, it is logged)</Label>
            <Input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="e.g. rates approved by the office" data-testid="adj-note" />
          </div>
          <Button onClick={guardar} disabled={guardando || !nota.trim()} data-testid="adj-save">
            {guardando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save adjustments
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
